import { useEffect, useRef } from "react";

const services = [
  {
    icon: "ri-compass-3-line",
    title: "Asesoramiento y Consultoría en Marketing",
    description: "Diagnóstico estratégico completo y plan de acción personalizado para potenciar el posicionamiento y crecimiento de tu marca.",
    featured: true,
    image: "https://readdy.ai/api/search-image?query=dark%20premium%20marketing%20strategy%20consultation%20business%20meeting%20professional%20luxury%20office%20golden%20light%20sophisticated%20corporate%20environment%20elegant%20minimalist&width=600&height=350&seq=svc-1&orientation=landscape",
  },
  {
    icon: "ri-rocket-2-line",
    title: "Marketing Digital y Crecimiento",
    description: "Estrategias integrales de SEO, SEM, email marketing y campañas de pago orientadas a resultados medibles.",
    featured: false,
  },
  {
    icon: "ri-code-s-slash-line",
    title: "Creación de Webs con IA",
    description: "Diseño y desarrollo de sitios web profesionales y de alto rendimiento potenciados por inteligencia artificial.",
    featured: false,
  },
  {
    icon: "ri-instagram-line",
    title: "Gestión de Redes Sociales",
    description: "Creación de contenido, estrategia editorial y gestión profesional de comunidades digitales para amplificar tu marca.",
    featured: false,
  },
  {
    icon: "ri-bar-chart-2-line",
    title: "Dashboards y Análisis de Datos",
    description: "Visualización avanzada de métricas y KPIs para tomar decisiones estratégicas basadas en datos reales.",
    featured: false,
  },
];

function ServiceCard({ service, index }: { service: typeof services[0]; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            el.style.transition = "opacity 0.7s ease, transform 0.7s ease";
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
          }, index * 100);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    el.style.opacity = "0";
    el.style.transform = "translateY(30px)";
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);

  if (service.featured) {
    return (
      <div
        ref={cardRef}
        className="lg:col-span-2 relative rounded-xl overflow-hidden border border-[#C9A227]/20 cursor-pointer group"
        style={{ background: "linear-gradient(135deg, #151515 0%, #0f0f0f 100%)" }}
      >
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover object-top opacity-15 group-hover:opacity-20 transition-opacity duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f0f0f]/90 via-[#0f0f0f]/70 to-transparent"></div>
        </div>
        <div className="relative z-10 p-10">
          <div className="inline-flex items-center gap-2 bg-[#C9A227]/10 border border-[#C9A227]/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-[#C9A227] text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Servicio estrella
            </span>
          </div>
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#C9A227]/10 border border-[#C9A227]/30 mb-5">
            <i className={`${service.icon} text-[#C9A227] text-2xl`}></i>
          </div>
          <h3
            className="text-white text-2xl font-bold mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {service.title}
          </h3>
          <p className="text-gray-400 text-base leading-relaxed max-w-lg" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
            {service.description}
          </p>
          <div className="mt-6 flex items-center gap-2 text-[#C9A227] text-sm font-semibold group-hover:gap-4 transition-all duration-200 cursor-pointer" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <span>Saber más</span>
            <i className="ri-arrow-right-line"></i>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className="relative rounded-xl border border-[#1f1f1f] hover:border-[#C9A227]/40 transition-all duration-300 p-8 cursor-pointer group"
      style={{ background: "#0a0a0a" }}
    >
      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1a1a1a] border border-[#2a2a2a] group-hover:border-[#C9A227]/30 transition-colors duration-300 mb-5">
        <i className={`${service.icon} text-[#C9A227] text-xl`}></i>
      </div>
      <h3
        className="text-white text-lg font-bold mb-3"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {service.title}
      </h3>
      <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
        {service.description}
      </p>
      <div className="mt-5 flex items-center gap-2 text-[#C9A227]/70 text-xs font-semibold group-hover:text-[#C9A227] group-hover:gap-3 transition-all duration-200" style={{ fontFamily: "'Montserrat', sans-serif" }}>
        <span>Saber más</span>
        <i className="ri-arrow-right-line"></i>
      </div>
    </div>
  );
}

export default function ServicesSection() {
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition = "opacity 0.8s ease, transform 0.8s ease";
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    el.style.opacity = "0";
    el.style.transform = "translateY(20px)";
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById("contacto");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="servicios" className="py-32" style={{ background: "#080808" }}>
      <div className="max-w-7xl mx-auto px-8 lg:px-16">

        {/* Header */}
        <div ref={titleRef} className="mb-16 text-center">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="text-[#C9A227] text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              / SERVICIOS
            </span>
          </div>
          <h2
            className="text-white text-4xl lg:text-5xl font-bold mb-5"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Soluciones Estratégicas para tu Marca
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
            Cada servicio está diseñado para generar impacto real y crecimiento sostenible en tu negocio.
          </p>
        </div>

        {/* Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={service.title} service={service} index={index} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <button
            onClick={scrollToContact}
            className="bg-[#C9A227] hover:bg-[#b8911e] text-black font-bold px-12 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer text-sm tracking-wide"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Solicita tu consultoría gratuita
          </button>
        </div>
      </div>
    </section>
  );
}
